import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase } from '../lib/typeHelper'

export interface CoiGeneratorUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

export interface CoiGeneratorPolicyFile {
  name: string
  url: string
}

export interface CoiGeneratorItem {
  id: string
  dateCreated: string
  dateUpdated: string
  hasCoi: boolean
  isCorrect: boolean | null
  policyFileName: string | null
  /**
   * A policy can be uploaded as several PDFs; policyFileName is the first.
   * Optional because a backend predating this field returns no key at all
   * (deploy skew), so every read must fall back.
   */
  policyFileCount?: number
  createdBy: CoiGeneratorUser | null
  reviewedBy: CoiGeneratorUser | null
}

export enum AgentRunStatus {
  Success = 'SUCCESS',
  Error = 'ERROR',
  Unavailable = 'UNAVAILABLE',
}

/** The LLM parse that produced this COI — for debugging a wrong certificate. */
export interface CoiGeneratorAgentRun {
  id: string
  adminUrl: string
  agentModel: string
  vendor: string
  status: AgentRunStatus
  latencyMs: number | null
  error: string
}

export interface CoiGeneratorDetail {
  id: string
  dateCreated: string
  dateUpdated: string
  metadata: Record<string, unknown>
  isCorrect: boolean | null
  hasCoi: boolean
  policyFileUrl: string | null
  /** Optional for the same deploy-skew reason as policyFileCount. */
  policyFiles?: CoiGeneratorPolicyFile[]
  coiFileUrl: string | null
  policyFileName: string | null
  coiFileName: string | null
  createdBy: CoiGeneratorUser | null
  reviewedBy: CoiGeneratorUser | null
  agentRun: CoiGeneratorAgentRun | null
}

interface UseCoiGeneratorListParams {
  page?: number
}

function getCoiGeneratorList(params: UseCoiGeneratorListParams): Promise<PaginatedResults<CoiGeneratorItem>> {
  const queryParams: Record<string, string> = {}
  if (params.page && params.page > 1) queryParams.page = String(params.page)

  return axios
    .get('/api/v1/insure/internal/coi-generator/', { params: queryParams })
    .then((response) => toCamelCase(response.data) as PaginatedResults<CoiGeneratorItem>)
}

export function useCoiGeneratorList(params: UseCoiGeneratorListParams = {}) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_GENERATOR, params.page],
    queryFn: () => getCoiGeneratorList(params),
  })
  return {
    items: data?.results ?? [],
    count: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: !!data?.previous,
    isLoading,
  }
}

function getCoiGeneratorDetail(id: string): Promise<CoiGeneratorDetail> {
  return axios
    .get(`/api/v1/insure/internal/coi-generator/${id}/`)
    .then((response) => toCamelCase(response.data) as CoiGeneratorDetail)
}

export function useCoiGeneratorDetail(id: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.COI_GENERATOR, id],
    queryFn: () => getCoiGeneratorDetail(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const item = query.state.data
      return item && !item.hasCoi ? 3000 : false
    },
  })
  return { item: data, isLoading }
}

export function useUploadPolicy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (files: File[]) => {
      const formData = new FormData()
      // Repeated 'file' entries — the endpoint reads them with getlist('file').
      files.forEach((file) => formData.append('file', file))
      return axios.post('/api/v1/insure/internal/coi-generator/upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_GENERATOR] })
    },
  })
}

export function useMarkCorrect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isCorrect }: { id: string; isCorrect: boolean }) => {
      return axios.patch(`/api/v1/insure/internal/coi-generator/${id}/mark-correct/`, {
        is_correct: isCorrect,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_GENERATOR] })
    },
  })
}
